import _ from 'lodash'
import { flushSync } from 'react-dom';
import { isEditingPaused, onEditingResumed } from '../utils/editingPause';
import { pushMessage } from '../utils/MessageBusUtils'
import { getChangedIds, getVersions } from './summaries'
import {
  getGroupMembers,
  getInvestments,
  getMarketDetails,
  getMarketGroups,
  getMarketStages,
  getMarketUsers
} from './markets';
import { getFetchSignaturesForMarket } from './versionSignatureUtils'
import { fetchComments } from './comments'
import { fetchInvestibles } from './marketInvestibles'
import { LimitedParallelMap } from '../utils/PromiseUtils'
import {
  checkInStorage,
  checkSignatureInStorage,
} from './storageIntrospector';
import LocalForageHelper from '../utils/LocalForageHelper'
import { COMMENTS_CONTEXT_NAMESPACE, commentsContextHack } from '../contexts/CommentsContext/CommentsContext';
import { investibleContextHack, INVESTIBLES_CONTEXT_NAMESPACE } from '../contexts/InvestibesContext/InvestiblesContext';
import { MARKET_CONTEXT_NAMESPACE, marketsContextHack } from '../contexts/MarketsContext/MarketsContext';
import {
  MARKET_PRESENCES_CONTEXT_NAMESPACE,
  marketPresencesContextHack
} from '../contexts/MarketPresencesContext/MarketPresencesContext';
import {
  MARKET_STAGES_CONTEXT_NAMESPACE,
  marketStagesContextHack
} from '../contexts/MarketStagesContext/MarketStagesContext';
import {
  MARKET_GROUPS_CONTEXT_NAMESPACE,
  marketGroupsContextHack
} from '../contexts/MarketGroupsContext/MarketGroupsContext';
import {
  GROUP_MEMBERS_CONTEXT_NAMESPACE,
  groupMembersContextHack
} from '../contexts/GroupMembersContext/GroupMembersContext';
import { RepeatingFunction } from '../utils/RepeatingFunction';
import { isSignedOut } from '../utils/userFunctions';
import { getMarketClient } from './marketLogin';
import { isInitialSyncComplete, markInitialSyncComplete } from './syncStatus';
import { TOKEN_TYPE_MARKET } from './tokenConstants';
import TokenStorageManager from '../authorization/TokenStorageManager';
import { addMarketsToStorage } from '../contexts/MarketsContext/marketsContextHelper';
import { addCommentsOther } from '../contexts/CommentsContext/commentsContextMessages';
import { updateCommentsFromVersions } from '../contexts/CommentsContext/commentsContextReducer';
import { refreshInvestibles } from '../contexts/InvestibesContext/investiblesContextHelper';
import { versionsUpdateMarketPresences } from '../contexts/MarketPresencesContext/marketPresencesContextReducer';
import { updateMarketStagesFromNetwork } from '../contexts/MarketStagesContext/marketStagesContextReducer';
import { addGroupsToStorage } from '../contexts/MarketGroupsContext/marketGroupsContextHelper';
import { versionsUpdateGroupMembers } from '../contexts/GroupMembersContext/groupMembersContextReducer';
import { markSync, timeSpan, timeSpanAsync } from '../utils/renderProfiler';
import {
  accrueMarketsStruct,
  getFirstAccruedMs,
  hasAccrued,
  maxHoldMs,
  msUntilRelease,
  msUntilSync,
  quietWindowMs,
  syncCapMs,
  syncQuietMs,
  overlayStorageStates,
  takeAccrued
} from './syncAccumulator';

const MAX_RETRIES = 10;
const MAX_DRIFT_TIME = 300000;
const MAX_CONCURRENT_API_CALLS = 5;
const MAX_CONCURRENT_ARCHIVE_API_CALLS = 1;
export const NOTIFICATIONS_HUB_CHANNEL = 'NotificationsChannel';
export const PUSH_MARKETS_CHANNEL = 'MarketsChannel';
export const PUSH_COMMENTS_CHANNEL = 'CommentsChannel';
export const PUSH_INVESTIBLES_CHANNEL = 'InvestiblesChannel';
export const PUSH_PRESENCE_CHANNEL = 'PresenceChannel';
export const PUSH_MEMBER_CHANNEL = 'MemberChannel';
// Channel used when you're banned. We purge your stuff from the local store if you are.
export const REMOVED_MARKETS_CHANNEL = 'RemovedMarketsChannel';
export const PUSH_STAGE_CHANNEL = 'MarketsStagesChannel';
export const PUSH_GROUPS_CHANNEL = 'MarketsGroupsChannel';
export const VERSIONS_EVENT = 'version_push';
export const BANNED_LIST = 'banned_list';
export class MatchError extends Error {}

let runner;
// Refresh state is local to the elected leader. Coalesce requests within that tab so a
// refresh requested mid-refresh runs exactly once afterward.
let refreshInProgress = false;
let refreshQueued = false;
let queuedDispatchers = undefined;
let queuedImmediateRelease = false;
let queuedAllowWhileEditing = false;
let editingRefresh;
// When the last refresh completed without error - lets speculative (focus/visibility/online)
// refreshes skip when the data is known fresh (C-all-1066).
let lastSuccessfulRefreshMs = 0;
// T-all-2485: a storm accrues instead of releasing per cycle, and one release feeds every
// context when the debounce judges it over (R-all-2316). Because nothing dispatches until
// then, the reducers also persist exactly once, which is what Q-all-478 O-2 settled.
let releasePending = false;
let lastCycleEndMs = 0;
let releaseTimer = undefined;
let releaseDispatchers = undefined;
// Initial sync is complete once a successful cycle's data has been released and nothing
// is left outstanding, not when some later cycle happens to find nothing new
// (https://stage.uclusion.com/dd56682c-9920-417b-be46-7a30d41bc905/Q-Marketing-258 O-4).
let successfulCycleAwaitingRelease = false;
let successfulCycleReleased = false;
// Incremented when this tab gives up leadership. Work started under an older value may
// finish its network calls, but it must not release data or restart the recurring runner.
let refreshLifecycle = 0;

function deferRefreshWhileEditing(dispatchers, releaseImmediately) {
  if (!editingRefresh) {
    let resolve;
    const promise = new Promise((done) => { resolve = done; });
    editingRefresh = { promise, resolve, releaseImmediately: false };
  }
  if (dispatchers) {
    editingRefresh.dispatchers = dispatchers;
  }
  editingRefresh.releaseImmediately ||= releaseImmediately;
  return editingRefresh.promise;
}

onEditingResumed(() => {
  if (editingRefresh) {
    const pending = editingRefresh;
    editingRefresh = undefined;
    matchErrorHandlingVersionRefresh(pending.dispatchers, pending.releaseImmediately)
      .then(pending.resolve);
  }
});

// Called on release and wherever outstanding sync work drains, so the order those finish in
// does not matter. A notification dependency that has used up its retries no longer counts.
function markCompleteIfCaughtUp() {
  if (!successfulCycleReleased || isInitialSyncComplete() || refreshInProgress || refreshQueued ||
      editingRefresh || syncThrottleTimer || releasePending || hasAccrued() ||
      !_.isEmpty(pendingPushChecks) || pendingNotificationChecks.some((check) => !check.exhausted)) {
    return;
  }
  markInitialSyncComplete();
}

function doRelease() {
  if (!releasePending) {
    return;
  }
  const firstAccruedMs = getFirstAccruedMs();
  const capExpired = firstAccruedMs !== undefined && Date.now() - firstAccruedMs >= maxHoldMs();
  // A cycle running means the storm is not quiet, so let its completion re-arm the timer.
  // The maximum hold overrides that - it exists precisely to fire mid-storm.
  if (refreshInProgress && !capExpired) {
    return;
  }
  releasePending = false;
  if (hasAccrued()) {
    const release = () => sendMarketsStruct(takeAccrued(), releaseDispatchers);
    timeSpan('release', () => {
      if (isInitialSyncComplete()) {
        release();
      } else {
        // Startup immediately runs another sync. Publish its data before that pass reads
        // React context, so a stale snapshot cannot leave loading until the drift timer.
        flushSync(release);
      }
    });
  }
  // Readiness follows the release, so the UI never reports ready ahead of the data.
  if (successfulCycleAwaitingRelease) {
    successfulCycleAwaitingRelease = false;
    successfulCycleReleased = true;
  }
  markCompleteIfCaughtUp();
  // Q-all-479 O-1: notifications release with the market data and never ahead of it, because
  // a row whose comment is not in state yet does not function. This fetch is asynchronous, so
  // calling it after the dispatch above keeps that order.
  refreshNotifications();
}

function scheduleRelease() {
  if (releaseTimer) {
    clearTimeout(releaseTimer);
  }
  const delay = msUntilRelease(Date.now(), lastCycleEndMs, getFirstAccruedMs(),
    quietWindowMs(), maxHoldMs());
  releaseTimer = setTimeout(() => {
    releaseTimer = undefined;
    doRelease();
  }, delay);
}
const matchErrorHandlingVersionRefresh = (dispatchers=undefined, releaseImmediately=false,
  allowWhileEditing=false) => {
  // A dead link burns 30s abort + retry per call and chains queued refreshes behind the
  // failures, so on bad internet the tab syncs back to back continuously. The browser
  // reliably knows definite offline - skip outright and let the 'online' listener or the
  // drift runner pick up when connectivity returns (C-all-1066).
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    console.info('Not refreshing while offline');
    return Promise.resolve(false);
  }
  if (!allowWhileEditing && isEditingPaused()) {
    return deferRefreshWhileEditing(dispatchers, releaseImmediately);
  }
  if (refreshInProgress) {
    refreshQueued = true;
    queuedImmediateRelease = queuedImmediateRelease || releaseImmediately;
    queuedAllowWhileEditing ||= allowWhileEditing;
    if (dispatchers) {
      queuedDispatchers = dispatchers;
    }
    return Promise.resolve(false);
  }
  const lifecycle = refreshLifecycle;
  refreshInProgress = true;
  let refreshSucceeded = true;
  // B-all-569: permanent sync-window marker, inert until window.__uclusionProfiler('on')
  markSync('start');
  return doVersionRefresh(dispatchers, () => lifecycle === refreshLifecycle)
    .catch((error) => {
      refreshSucceeded = false;
      if (error instanceof MatchError) {
        // just log match problems
        console.info('Ignoring match error');
        console.info(error);
      } else {
        // Nowhere to raise this to so just put out as error
        console.info('doVersionRefresh error');
        console.error(error);
      }
    })
    .then((dirtyMarketCount) => {
      markSync('end');
      if (lifecycle !== refreshLifecycle) {
        refreshInProgress = false;
        releasePending = false;
        releaseDispatchers = undefined;
        successfulCycleAwaitingRelease = false;
        if (hasAccrued()) {
          takeAccrued();
        }
        if (refreshQueued) {
          refreshQueued = false;
          const dispatchersForQueued = queuedDispatchers;
          const releaseQueuedImmediately = queuedImmediateRelease;
          const allowQueuedWhileEditing = queuedAllowWhileEditing;
          queuedDispatchers = undefined;
          queuedImmediateRelease = false;
          queuedAllowWhileEditing = false;
          return matchErrorHandlingVersionRefresh(dispatchersForQueued, releaseQueuedImmediately,
            allowQueuedWhileEditing);
        }
        return dirtyMarketCount;
      }
      if (refreshSucceeded) {
        lastSuccessfulRefreshMs = Date.now();
        successfulCycleAwaitingRelease = true;
      }
      refreshInProgress = false;
      lastCycleEndMs = Date.now();
      releasePending = true;
      if (refreshQueued) {
        refreshQueued = false;
        const dispatchersForQueued = queuedDispatchers;
        const releaseQueuedImmediately = releaseImmediately || queuedImmediateRelease;
        const allowQueuedWhileEditing = queuedAllowWhileEditing;
        queuedDispatchers = undefined;
        queuedImmediateRelease = false;
        queuedAllowWhileEditing = false;
        if (isEditingPaused() && !allowQueuedWhileEditing) {
          deferRefreshWhileEditing(dispatchersForQueued, releaseQueuedImmediately);
          // Finish this admitted cycle even though its successor now waits for editing.
        } else {
          return matchErrorHandlingVersionRefresh(dispatchersForQueued, releaseQueuedImmediately,
            allowQueuedWhileEditing);
        }
      }
      if (releaseImmediately) {
        if (releaseTimer) {
          clearTimeout(releaseTimer);
          releaseTimer = undefined;
        }
        doRelease();
      } else {
        scheduleRelease();
      }
      // undefined after an error (the catch above), a number after a clean run
      return dirtyMarketCount;
    });
};
// Stage one (R-all-2334): a burst of pushes must produce one discovery pass rather than one per
// push. refreshInProgress/refreshQueued only coalesce while a cycle is already running, which does
// nothing for pushes arriving 2.4s apart - exactly what R-all-2331 measured, nineteen cycles each
// asking versions about a single market id. This throttles the entry: leading edge so a lone
// notification is never delayed, then one trailing pass for everything that arrived during the
// window, so the dirty markets batch into one versions call and the existing chunking finally engages.
let lastRequestMs = undefined;
let syncThrottleTimer = undefined;
let firstSuppressedMs = undefined;
let suppressedDispatchers = undefined;
let throttledRefreshResult;

function settleThrottledRefresh(refresh) {
  const pending = throttledRefreshResult;
  throttledRefreshResult = undefined;
  if (pending) {
    refresh.then(pending.resolve);
  }
  return refresh;
}

function runCycleNow(dispatchers) {
  firstSuppressedMs = undefined;
  return matchErrorHandlingVersionRefresh(dispatchers);
}

function throttledRefresh(dispatchers) {
  if (isEditingPaused()) {
    if (syncThrottleTimer) {
      clearTimeout(syncThrottleTimer);
      syncThrottleTimer = undefined;
    }
    const pendingDispatchers = dispatchers || suppressedDispatchers;
    suppressedDispatchers = undefined;
    return settleThrottledRefresh(runCycleNow(pendingDispatchers));
  }
  const now = Date.now();
  // A cycle already running means this request must wait, whatever the window says. Calling
  // through would hand it to matchErrorHandlingVersionRefresh's own refreshQueued path, which
  // re-enters directly at the tail of that cycle and so skips the debounce entirely - the
  // sub-second cluster of /versioned calls burst eleven recorded.
  const delay = refreshInProgress ? syncQuietMs()
    : msUntilSync(now, lastRequestMs, firstSuppressedMs, syncQuietMs(), syncCapMs());
  lastRequestMs = now;
  if (delay === 0) {
    if (syncThrottleTimer) {
      clearTimeout(syncThrottleTimer);
      syncThrottleTimer = undefined;
    }
    return settleThrottledRefresh(runCycleNow(dispatchers));
  }
  if (firstSuppressedMs === undefined) {
    firstSuppressedMs = now;
  }
  if (dispatchers) {
    suppressedDispatchers = dispatchers;
  }
  // Reset the wait on every request - that is what makes this a debounce rather than a rate
  // limiter, and it is what lets a drip of version changes settle into a single discovery pass.
  if (syncThrottleTimer) {
    clearTimeout(syncThrottleTimer);
  }
  if (!throttledRefreshResult) {
    let resolve;
    const promise = new Promise((done) => { resolve = done; });
    throttledRefreshResult = { promise, resolve };
  }
  syncThrottleTimer = setTimeout(() => {
    syncThrottleTimer = undefined;
    if (refreshInProgress) {
      // Still running - re-arm rather than queue behind it, for the same reason as above.
      throttledRefresh(suppressedDispatchers);
      return;
    }
    const trailingDispatchers = suppressedDispatchers;
    suppressedDispatchers = undefined;
    settleThrottledRefresh(runCycleNow(trailingDispatchers))
      .catch(() => console.warn('Error in throttled refresh'));
  }, delay);
  return throttledRefreshResult.promise;
}

export function startRefreshRunner() {
  runner = new RepeatingFunction(throttledRefresh, MAX_DRIFT_TIME, MAX_RETRIES);
  return runner.start();
}

/**
 * Starts the drift runner if this tab does not have one, otherwise does nothing.
 * Lets periodic callers guarantee max drift without stacking an extra refresh on top of
 * the runner's own interval (C-all-1066 collapsed the duplicate 5 minute repeaters).
 */
export function ensureRefreshRunner() {
  if (runner == null) {
    return startRefreshRunner();
  }
}

export function stopRefreshRunner() {
  refreshLifecycle += 1;
  if (editingRefresh) {
    editingRefresh.resolve(false);
    editingRefresh = undefined;
  }
  if (throttledRefreshResult) {
    throttledRefreshResult.resolve(false);
    throttledRefreshResult = undefined;
  }
  if (runner) {
    runner.stop();
    runner = undefined;
  }
  if (syncThrottleTimer) {
    clearTimeout(syncThrottleTimer);
    syncThrottleTimer = undefined;
  }
  if (releaseTimer) {
    clearTimeout(releaseTimer);
    releaseTimer = undefined;
  }
  releasePending = false;
  releaseDispatchers = undefined;
  successfulCycleAwaitingRelease = false;
  successfulCycleReleased = false;
  if (hasAccrued()) {
    takeAccrued();
  }
  lastRequestMs = undefined;
  firstSuppressedMs = undefined;
  suppressedDispatchers = undefined;
  refreshQueued = false;
  queuedDispatchers = undefined;
  queuedImmediateRelease = false;
  queuedAllowWhileEditing = false;
  removePendingPushChecks(() => true);
  notificationVerifyResume?.();
  notificationVerifyResume = undefined;
  if (notificationVerifyTimer) {
    clearTimeout(notificationVerifyTimer);
    notificationVerifyTimer = undefined;
  }
  pendingNotificationChecks.splice(0, pendingNotificationChecks.length);
}

function ensureRunnerAfter(refreshPromise) {
  const lifecycle = refreshLifecycle;
  return refreshPromise.then((dirtyMarketCount) => {
    if (lifecycle === refreshLifecycle && runner == null) {
      // The runner's first background pass may wait for editing. This refresh is complete.
      startRefreshRunner().catch(() => console.warn('Error starting refresh runner'));
    }
    return dirtyMarketCount;
  });
}

/**
 * Executes one refresh without the burst debounce, while retaining the shared
 * in-progress/queued guard and release scheduling.
 * @param dispatchers
 * @returns {Promise<*>}
 */
export function refreshVersionsNow(dispatchers=undefined, allowWhileEditing=false) {
  if (isSignedOut()) {
    console.info('Not refreshing when signed out')
    return Promise.resolve(true);
  }
  return ensureRunnerAfter(matchErrorHandlingVersionRefresh(dispatchers, true, allowWhileEditing));
}

/**
 * Executes one immediate refresh without starting this tab's drift runner. Followers use this
 * only as the bounded B-all-446 liveness fallback when leadership cannot be established.
 */
export function refreshVersionsOnce(dispatchers=undefined) {
  if (isSignedOut()) {
    console.info('Not refreshing when signed out')
    return Promise.resolve(true);
  }
  const lifecycle = refreshLifecycle;
  return matchErrorHandlingVersionRefresh(dispatchers, true).then((dirtyMarketCount) => {
    // This follower has no recurring runner. Its successful full fetch and immediate
    // release provide a usable snapshot, as populated disk adoption does for a warm tab.
    if (lifecycle === refreshLifecycle && !isSignedOut() &&
        typeof dirtyMarketCount === 'number' && dirtyMarketCount >= 0) {
      markInitialSyncComplete();
    }
    return dirtyMarketCount;
  });
}

/**
 * Executes a version refresh, coalescing with any refresh already running in this tab,
 * and makes sure a refreshRunner is started so max drift is honored
 * @param dispatchers
 * @param skipIfRefreshedWithinMs marks the refresh speculative (nothing says data changed -
 *        e.g. window focus): skip when a refresh is already in flight or one succeeded
 *        within this many millis, instead of queueing another full cycle
 * @returns {Promise<*>}
 */
export function refreshVersions (dispatchers=undefined, skipIfRefreshedWithinMs=undefined) {
  if (isSignedOut()) {
    console.info('Not refreshing when signed out')
    return Promise.resolve(true); // also do nothing when signed out
  }
  if (skipIfRefreshedWithinMs !== undefined &&
      (refreshInProgress || Date.now() - lastSuccessfulRefreshMs < skipIfRefreshedWithinMs)) {
    console.info('Skipping speculative refresh - already fresh or in progress');
    return Promise.resolve(true);
  }
  return ensureRunnerAfter(throttledRefresh(dispatchers));
}

// Verified push sync (T-all-2259): a push names the exact object and version that caused
// it, so instead of trusting one refresh we keep refreshing with backoff until that object
// is actually in storage - covering the async version indicator write racing the refresh
// (T-all-2252). Capped; the drift runner remains the final backstop.
const PUSH_VERIFY_MAX_ATTEMPTS = 5;
const PUSH_VERIFY_BASE_DELAY_MS = 2000;
const pendingPushChecks = [];
let pushVerifyTimer = undefined;
let pushVerifyResume;

function removePendingPushChecks(predicate) {
  _.remove(pendingPushChecks, predicate).forEach((check) => clearTimeout(check.expiryTimer));
  if (_.isEmpty(pendingPushChecks)) {
    clearTimeout(pushVerifyTimer);
    pushVerifyTimer = undefined;
    pushVerifyResume?.();
    pushVerifyResume = undefined;
    markCompleteIfCaughtUp();
  }
}

function pruneExpiredStageGuessChecks() {
  removePendingPushChecks((check) => check.stageGuessDeadline !== undefined &&
    check.stageGuessDeadline <= Date.now());
}

function signatureForPush(push) {
  const { objectType, version, objectIdOneTwo } = push;
  // object_id_one_two is `${object_id_one}_${object_id_two}` for pair types (investment,
  // group_capability, market_investible) and just object_id_one otherwise (Q-all-193 O-1).
  const [objectIdOne, objectIdTwo] = objectIdOneTwo.split('_');
  const signature = { object_type: objectType, version, object_id_one: objectIdOne };
  if (objectIdTwo) {
    signature.object_id_two = objectIdTwo;
  }
  return signature;
}

function verifyPendingPushes() {
  pruneExpiredStageGuessChecks();
  if (_.isEmpty(pendingPushChecks)) {
    return;
  }
  return getStorageStates().then((storageStates) => {
    pruneExpiredStageGuessChecks();
    removePendingPushChecks((check) => {
      if (checkSignatureInStorage(check.marketId, check.signature, storageStates)) {
        return true;
      }
      if (check.attempts >= PUSH_VERIFY_MAX_ATTEMPTS) {
        console.warn('Giving up waiting for pushed object - drift runner will cover');
        console.warn(check.signature);
        return true;
      }
      return false;
    });
    if (_.isEmpty(pendingPushChecks) || pushVerifyTimer || pushVerifyResume) {
      return;
    }
    // Back off on the youngest check so a fresh push keeps the next retry snappy
    const attempts = Math.min(...pendingPushChecks.map((check) => check.attempts));
    const delay = PUSH_VERIFY_BASE_DELAY_MS * Math.pow(2, attempts);
    console.info(`Pushed object not in storage yet - retrying sync in ${delay}ms`);
    const retry = () => {
      pushVerifyTimer = undefined;
      pruneExpiredStageGuessChecks();
      if (_.isEmpty(pendingPushChecks)) {
        return;
      }
      if (isEditingPaused()) {
        pushVerifyResume = onEditingResumed(() => {
          pushVerifyResume();
          pushVerifyResume = undefined;
          retry();
        });
        return;
      }
      const pendingRetries = [...pendingPushChecks];
      refreshVersions()
        .then((dirtyMarketCount) => {
          if (typeof dirtyMarketCount === 'number' || dirtyMarketCount === undefined) {
            pendingRetries.forEach((check) => { check.attempts += 1; });
          }
          return verifyPendingPushes();
        })
        .catch(() => console.warn('Error in push verify refresh'));
    };
    pushVerifyTimer = setTimeout(retry, delay);
  });
}

/**
 * Refresh for a server push. When the push payload identifies the changed object
 * (object_type/market id/version/object_id_one_two per T-all-2259), refresh and then
 * verify that object landed in storage, retrying with backoff until it does.
 * @param push {objectType, marketId, version, objectIdOneTwo} - omit for pushes that
 *        do not carry a market object (e.g. notification events). A local stage guess also
 *        supplies stageGuessDeadline so only its own check expires with the guess.
 */
export function refreshVersionsFromPush(push=undefined) {
  const stageGuessDeadline = push?.stageGuessDeadline;
  if (stageGuessDeadline !== undefined && stageGuessDeadline <= Date.now()) {
    return Promise.resolve(false);
  }
  if (push?.objectIdOneTwo && push?.version !== undefined && push?.marketId) {
    const signature = signatureForPush(push);
    if (stageGuessDeadline !== undefined && pendingPushChecks.some((check) =>
      check.marketId === push.marketId && check.stageGuessDeadline === stageGuessDeadline &&
      _.isEqual(check.signature, signature))) {
      return Promise.resolve(false);
    }
    const check = { marketId: push.marketId, signature, attempts: 0, stageGuessDeadline };
    pendingPushChecks.push(check);
    if (stageGuessDeadline !== undefined) {
      check.expiryTimer = setTimeout(() => removePendingPushChecks((candidate) => candidate === check),
        stageGuessDeadline - Date.now());
    }
  }
  return refreshVersions().then((dirtyMarketCount) => {
    verifyPendingPushes();
    return dirtyMarketCount;
  });
}

// Unlike a pushed object, a notification marker is satisfied only when the reporting tab's
// messageIsSynced pass removes it. Keeping this retry state separate preserves push timing.
const pendingNotificationChecks = [];
let notificationVerifyTimer = undefined;
let notificationVerifyResume;
const NOTIFICATION_DEPENDENCY_LEASE_MS = MAX_DRIFT_TIME * 2;

function notificationCheckKey(check) {
  return check.commentId
    ? ['c', check.marketId, check.commentId, check.version ?? ''].join('|')
    : ['i', check.marketId, check.investibleId, check.version ?? ''].join('|');
}

function pruneExpiredNotificationChecks(now=Date.now()) {
  const expired = _.remove(pendingNotificationChecks, (check) => check.expiresAt <= now);
  if (_.isEmpty(pendingNotificationChecks) && notificationVerifyTimer) {
    clearTimeout(notificationVerifyTimer);
    notificationVerifyTimer = undefined;
  }
  if (!_.isEmpty(expired)) {
    markCompleteIfCaughtUp();
  }
}

function scheduleNotificationVerification(resetTimer=false) {
  pruneExpiredNotificationChecks();
  if (resetTimer && notificationVerifyTimer) {
    clearTimeout(notificationVerifyTimer);
    notificationVerifyTimer = undefined;
  }
  const retryableChecks = pendingNotificationChecks.filter((check) => !check.exhausted);
  if (_.isEmpty(retryableChecks) || notificationVerifyTimer || notificationVerifyResume) {
    return;
  }
  const attempts = Math.min(...retryableChecks.map((check) => check.attempts));
  const delay = PUSH_VERIFY_BASE_DELAY_MS * Math.pow(2, attempts);
  console.info(`Notification dependency still missing - retrying sync in ${delay}ms`);
  const retry = () => {
    notificationVerifyTimer = undefined;
    if (isEditingPaused()) {
      notificationVerifyResume = onEditingResumed(() => {
        notificationVerifyResume();
        notificationVerifyResume = undefined;
        retry();
      });
      return;
    }
    const pendingRetries = pendingNotificationChecks.filter((check) => !check.exhausted);
    if (_.isEmpty(pendingRetries)) {
      return;
    }
    refreshVersions().then((dirtyMarketCount) => {
      if (typeof dirtyMarketCount === 'number' || dirtyMarketCount === undefined) {
        pendingRetries.forEach((check) => { check.attempts += 1; });
      }
      pendingRetries.filter((check) => pendingNotificationChecks.includes(check) &&
        check.attempts >= PUSH_VERIFY_MAX_ATTEMPTS)
        .forEach((check) => {
          check.exhausted = true;
          console.warn('Giving up automatic retries for notification dependency - later syncs will still force it');
          console.warn({ marketId: check.marketId, commentId: check.commentId,
            investibleId: check.investibleId, version: check.version });
        });
      scheduleNotificationVerification();
      markCompleteIfCaughtUp();
    }).catch(() => console.warn('Error in notification dependency refresh'));
  };
  notificationVerifyTimer = setTimeout(retry, delay);
}

/**
 * messageIsSynced has already identified these notification dependencies as absent. Hold their
 * markets known dirty independently of the latest audit signature, then use the normal market
 * version path to reconcile them. Snapshots are scoped to the reporting tab so a lagging tab
 * cannot clear another tab's marker; a later synced snapshot from that tab retires its markers.
 * S-all-283: an unsynced job or assignment notification records market plus investible rather
 * than a comment, and that form is enough because this path forces the market, not the object.
 */
export async function refreshVersionsForNotificationDependencies(dependencies=[], dispatchers=undefined,
  sourceId='local') {
  pruneExpiredNotificationChecks();
  const expiresAt = Date.now() + NOTIFICATION_DEPENDENCY_LEASE_MS;
  const checks = dependencies.filter((dependency) => dependency?.marketId &&
    (dependency.commentId || dependency.investibleId))
    .map((dependency) => ({
      marketId: dependency.marketId,
      commentId: dependency.commentId,
      investibleId: dependency.investibleId,
      version: dependency.version,
      sourceId,
      expiresAt
    }));
  const desiredKeys = new Set(checks.map(notificationCheckKey));
  _.remove(pendingNotificationChecks, (check) => check.sourceId === sourceId &&
    !desiredKeys.has(notificationCheckKey(check)));
  let added = false;
  checks.forEach((check) => {
    const existing = pendingNotificationChecks.find((candidate) => candidate.sourceId === sourceId &&
      notificationCheckKey(candidate) === notificationCheckKey(check));
    if (existing) {
      existing.expiresAt = expiresAt;
    } else {
      pendingNotificationChecks.push({ ...check, attempts: 0 });
      added = true;
    }
  });
  if (_.isEmpty(pendingNotificationChecks) && notificationVerifyTimer) {
    clearTimeout(notificationVerifyTimer);
    notificationVerifyTimer = undefined;
  }
  if (!added) {
    // A later snapshot from this tab may have retired the last dependency still being retried.
    markCompleteIfCaughtUp();
    return false;
  }
  const dirtyMarketCount = await refreshVersionsNow(dispatchers);
  scheduleNotificationVerification(true);
  return dirtyMarketCount;
}

export function refreshNotifications () {
  if (!isSignedOut()) {
    // check if new notifications
    pushMessage(NOTIFICATIONS_HUB_CHANNEL, { event: VERSIONS_EVENT, refreshLifecycle });
  }
}

export function isRefreshLifecycleCurrent(lifecycle) {
  return lifecycle === undefined || lifecycle === refreshLifecycle;
}

/**
 * Updates all our markets from the passed in signatures
 * @param marketIds the market ids to fetch signatures for
 * @param marketsStruct for passing the gathered information
 * @param maxConcurrentCount the maximum number of api calls to make at once
 * @param storageStates
 * @param isInline whether the markets are inline or not
 * @param refreshIsCurrent whether this refresh still belongs to the active leadership lifecycle
 * @returns {Promise<*>}
 */
export function updateMarkets(marketIds, marketsStruct, maxConcurrentCount, storageStates,
  isInline=false, refreshIsCurrent=() => true) {
  if (_.isEmpty(marketIds) || !refreshIsCurrent()) {
    return Promise.resolve(true);
  }
  return getVersions(marketIds, isInline)
    .then((marketSignatures) => {
      if (!refreshIsCurrent()) {
        return [];
      }
      //console.error(marketSignatures);
      return new LimitedParallelMap(marketSignatures, (marketSignature) => {
        if (!refreshIsCurrent()) {
          return Promise.resolve(false);
        }
        //console.error("MarketSignature")
        //console.error(marketSignature);
        const { market_id: marketId, signatures: componentSignatures } = marketSignature;
        return doRefreshMarket(marketId, componentSignatures, marketsStruct, storageStates,
          refreshIsCurrent);
      }, maxConcurrentCount);
    });
}

export function sendMarketsStruct(marketsStruct, dispatchers) {
  console.info('Updating with markets struct');
  const { marketsDispatch, marketStagesDispatch, groupsDispatch, presenceDispatch, groupMembersDispatch,
    investiblesDispatch, commentsDispatch, diffDispatch, index, ticketsDispatch } = dispatchers || {};
  if (marketsStruct['markets']) {
    console.info(marketsStruct['markets']);
    if (marketsDispatch) {
      addMarketsToStorage(marketsDispatch, marketsStruct['markets']);
    } else {
      pushMessage(PUSH_MARKETS_CHANNEL, { event: VERSIONS_EVENT, marketDetails: marketsStruct['markets'] });
    }
  }
  if (marketsStruct['comments']) {
    if (commentsDispatch) {
      let allComments = [];
      Object.values(marketsStruct['comments']).forEach((comments) => allComments = allComments.concat(comments));
      console.info(`Processing comments length ${allComments.length}`);
      addCommentsOther(diffDispatch, index, ticketsDispatch, allComments);
      console.info(`Dispatching comments with existing length ${marketsStruct['existingCommentIds']?.length}`);
      commentsDispatch(updateCommentsFromVersions(marketsStruct['comments'], marketsStruct['existingCommentIds']));
    } else {
      console.info('Pushing comments struct');
      pushMessage(PUSH_COMMENTS_CHANNEL, {
        event: VERSIONS_EVENT, commentDetails: marketsStruct['comments'],
        existingCommentIds: marketsStruct['existingCommentIds']
      });
    }
  }
  if (marketsStruct['investibles']) {
    console.info(marketsStruct['investibles']);
    if (investiblesDispatch) {
      refreshInvestibles(investiblesDispatch, diffDispatch, marketsStruct['investibles'], true);
    } else {
      pushMessage(PUSH_INVESTIBLES_CHANNEL, { event: VERSIONS_EVENT,
        investibles: marketsStruct['investibles'] });
    }
  }
  if (marketsStruct['users']) {
    console.info(marketsStruct['users']);
    if (presenceDispatch) {
      presenceDispatch(versionsUpdateMarketPresences(marketsStruct['users']));
    } else {
      pushMessage(PUSH_PRESENCE_CHANNEL, { event: VERSIONS_EVENT, userDetails: marketsStruct['users'] });
    }
  }
  if (marketsStruct['stages']) {
    console.info(marketsStruct['stages']);
    if (marketStagesDispatch) {
      marketStagesDispatch(updateMarketStagesFromNetwork(marketsStruct['stages']));
    } else {
      pushMessage(PUSH_STAGE_CHANNEL, { event: VERSIONS_EVENT, stageDetails: marketsStruct['stages'] });
    }
  }
  if (marketsStruct['group']) {
    console.info(marketsStruct['group']);
    if (groupsDispatch) {
      addGroupsToStorage(groupsDispatch, marketsStruct['group']);
    } else {
      pushMessage(PUSH_GROUPS_CHANNEL, { event: VERSIONS_EVENT, groupDetails: marketsStruct['group'] });
    }
  }
  if (marketsStruct['members']) {
    console.info(marketsStruct['members']);
    if (groupMembersDispatch) {
      groupMembersDispatch(versionsUpdateGroupMembers(marketsStruct['members']));
    } else {
      pushMessage(PUSH_MEMBER_CHANNEL, { event: VERSIONS_EVENT, memberDetails: marketsStruct['members'] });
    }
  }
}

function addMarketsStructInfo(infoType, marketsStruct, details, marketId) {
  if (_.isEmpty(details)) {
    return;
  }
  if (marketId) {
    if (!marketsStruct[infoType]) {
      marketsStruct[infoType] = {};
    }
    if (!marketsStruct[infoType][marketId]) {
      marketsStruct[infoType][marketId] = details;
    } else {
      marketsStruct[infoType][marketId] = marketsStruct[infoType][marketId].concat(details);
    }
  } else {
    if (!marketsStruct[infoType]) {
      marketsStruct[infoType] = [];
    }
    marketsStruct[infoType] = marketsStruct[infoType].concat(details);
  }
}

export function getStorageStates() {
  const storageStates = {};
  // Reloading comments and investibles from disk will take up too much memory and confuse leader strategy
  // So local forage helper will use the memory pointer if it is initialized
  const helper = new LocalForageHelper(COMMENTS_CONTEXT_NAMESPACE);
  return helper.getState(commentsContextHack).then((state) => {
    storageStates.commentsState = state || {};
    const helper = new LocalForageHelper(INVESTIBLES_CONTEXT_NAMESPACE);
    return helper.getState(investibleContextHack);
  }).then((state) => {
    storageStates.investiblesState = state || {};
    const helper = new LocalForageHelper(MARKET_CONTEXT_NAMESPACE);
    return helper.getState(marketsContextHack);
  }).then((state) => {
    storageStates.marketsState = state || {};
    const helper = new LocalForageHelper(MARKET_PRESENCES_CONTEXT_NAMESPACE);
    return helper.getState(marketPresencesContextHack);
  }).then((state) => {
    storageStates.marketPresencesState = state || {};
    const helper = new LocalForageHelper(MARKET_STAGES_CONTEXT_NAMESPACE);
    return helper.getState(marketStagesContextHack);
  }).then((state) => {
    storageStates.marketStagesState = state || {};
    const helper = new LocalForageHelper(MARKET_GROUPS_CONTEXT_NAMESPACE);
    return helper.getState(marketGroupsContextHack);
  }).then((state) => {
    storageStates.marketGroupsState = state ?? {};
    const helper = new LocalForageHelper(GROUP_MEMBERS_CONTEXT_NAMESPACE);
    return helper.getState(groupMembersContextHack);
  }).then((state) => {
    storageStates.groupMembersState = state ?? {};
    // R-all-2308: getState short-circuits to the in-memory context state, so without this
    // overlay every accrued market would look dirty again on the next cycle and be refetched.
    return overlayStorageStates(storageStates);
  });
}

/**
 * Function that will make exactly one attempt to sync
 * @returns {Promise<*>}
 */
async function doVersionRefresh(dispatchers, refreshIsCurrent) {
  console.info('Checking for sync');
  releaseDispatchers = dispatchers;
  const storageStates = await timeSpanAsync('getStorageStates', () => getStorageStates());
  if (!refreshIsCurrent()) {
    return 0;
  }
  const audits = await timeSpanAsync('getChangedIds', () => getChangedIds());
  if (!refreshIsCurrent()) {
    return 0;
  }
  const foregroundList = [];
  const backgroundList = [];
  const bannedList = [];
  const inlineList = [];
  const bannedPromises = [];
  const forcedMarketIds = new Set();
  pruneExpiredNotificationChecks();
  pendingNotificationChecks.forEach((check) => forcedMarketIds.add(check.marketId));
  // The last audit may name an already-synced vote while a pushed resolution or stage
  // change is still missing. That exact missing signature remains evidence of dirty data.
  pruneExpiredStageGuessChecks();
  pendingPushChecks.forEach((check) => {
    if (!checkSignatureInStorage(check.marketId, check.signature, storageStates)) {
      forcedMarketIds.add(check.marketId);
    }
  });
  timeSpan('signatureDiff', () => (audits || []).forEach((audit) => {
    const { signature, inline, active, banned, id } = audit;
    if (banned) {
      forcedMarketIds.delete(id);
      bannedList.push(id);
      const tokenStorageManager = new TokenStorageManager();
      bannedPromises.push(tokenStorageManager.deleteToken(TOKEN_TYPE_MARKET, id, refreshIsCurrent));
    } else if (forcedMarketIds.has(id) || !checkSignatureInStorage(id, signature, storageStates, true)) {
      forcedMarketIds.delete(id);
      if (inline) {
        inlineList.push(id);
      } else if (active) {
        foregroundList.push(id);
      } else {
        backgroundList.push(id);
      }
    }
  }));
  // Missing pushed objects and notification dependencies remain authoritative even when the
  // audit row is absent. Pending checks retry object-version rows that are not visible yet
  // through this same normal market refresh path.
  forcedMarketIds.forEach((marketId) => foregroundList.push(marketId));
  // T-all-2485: this fired every cycle regardless, and the presences listener answered it with a
  // dispatch whose reducer correctly returned the same state - which the wrapper then persisted
  // anyway. Burst eight measured nineteen whole-state presence writes for a storm in which nothing
  // was banned. Nobody needs telling about an empty list.
  if (!_.isEmpty(bannedList) && refreshIsCurrent()) {
    pushMessage(REMOVED_MARKETS_CHANNEL, { event: BANNED_LIST, bannedList });
  }
  await Promise.all(bannedPromises);
  if (!refreshIsCurrent()) {
    return 0;
  }
  // Starting operation in progress just presents as a bug to the user because freezes all buttons so just log
  console.info('Beginning inline versions update');
  console.info(inlineList);
  // TODO: this is evil. We're using the inlineMarketsStruct as an _output_ parameter that gets mutated
  const inlineMarketsStruct = {};
  await timeSpanAsync('updateMarkets:inline', () => updateMarkets(inlineList, inlineMarketsStruct,
    MAX_CONCURRENT_API_CALLS, storageStates, true, refreshIsCurrent))
  if (!refreshIsCurrent()) {
    return 0;
  }
  accrueMarketsStruct(inlineMarketsStruct);
  const foregroundMarketsStruct = {};
  console.info('Beginning foreground versions update');
  console.info(foregroundList);
  // TODO: Again, this is evil. ForegroundMarketsStruct is an _output_ parameter that gets mutated
  await timeSpanAsync('updateMarkets:foreground', () => updateMarkets(foregroundList,
    foregroundMarketsStruct, MAX_CONCURRENT_API_CALLS, storageStates, false, refreshIsCurrent));
  if (!refreshIsCurrent()) {
    return 0;
  }
  accrueMarketsStruct(foregroundMarketsStruct);
  const backgroundMarketsStruct = {};
  console.info('Finished foreground update');
  console.info('Beginning background versions update');
  console.info(backgroundList);
  await timeSpanAsync('updateMarkets:background', () => updateMarkets(backgroundList,
    backgroundMarketsStruct, MAX_CONCURRENT_ARCHIVE_API_CALLS, storageStates, false, refreshIsCurrent));
  if (!refreshIsCurrent()) {
    return 0;
  }
  accrueMarketsStruct(backgroundMarketsStruct);
  console.info('Ending versions update');
  // How many markets were dirty - lets push-triggered callers detect a refresh that
  // raced the async version indicator write and found nothing (T-all-2252).
  return inlineList.length + foregroundList.length + backgroundList.length;
}

/**
 * Refreshes a given market using the component signatures to map against
 * @param marketId the market id to refresh
 * @param componentSignatures the component signatures telling us what we're looking for
 * @param marketsStruct
 * @param storageStates
 * @param refreshIsCurrent whether this refresh still belongs to the active leadership lifecycle
 * @returns {null}
 */
async function doRefreshMarket(marketId, componentSignatures, marketsStruct, storageStates,
  refreshIsCurrent) {
  const serverFetchSignatures = getFetchSignaturesForMarket(componentSignatures);
  const fromStorage = checkInStorage(marketId, serverFetchSignatures, storageStates);
  const { markets, comments, marketPresences, marketStages, investibles, marketGroups, groupMembers } = fromStorage;
  const promises = [];
  const marketClient = await getMarketClient(marketId);
  if (!marketClient || !refreshIsCurrent()) {
    return promises;
  }
  if (!_.isEmpty(markets.unmatchedSignatures)) {
    // can only be one :)
    promises.push(fetchMarketVersion(marketClient, marketId, marketsStruct));
  }
  if (!_.isEmpty(comments.unmatchedSignatures)) {
    promises.push(fetchMarketComments(marketClient, marketId, comments, marketsStruct));
  }
  if (!_.isEmpty(investibles.unmatchedSignatures)) {
    promises.push(fetchMarketInvestibles(marketClient, marketId, investibles, marketsStruct));
  }
  if (!_.isEmpty(marketPresences.unmatchedSignatures)) {
    promises.push(fetchMarketPresences(marketClient, marketId, marketPresences, marketsStruct));
  }
  if (!_.isEmpty(marketStages.unmatchedSignatures)) {
    promises.push(fetchMarketStages(marketClient, marketId, marketStages, marketsStruct));
  }
  if (!_.isEmpty(marketGroups.unmatchedSignatures)) {
    promises.push(fetchMarketGroups(marketClient, marketId, marketGroups, marketsStruct));
  }
  if (!_.isEmpty(groupMembers.unmatchedSignatures)) {
    promises.push(fetchGroupMembers(marketClient, marketId, groupMembers, marketsStruct));
  }
  return new LimitedParallelMap(promises, (promise) => promise, MAX_CONCURRENT_API_CALLS);
}

function fetchMarketVersion(marketClient, marketId, marketsStruct) {
  // No need to pass signatures here as it's already a consistent read
  return getMarketDetails(marketClient)
    .then((marketDetails) => {
      // we bothered to fetch the data, so we should use it:)
      addMarketsStructInfo('markets', marketsStruct, [marketDetails]);
    });
}

function fetchMarketComments(marketClient, marketId, allComments, marketsStruct) {
  // This call already has signatures for parent reply added if necessary
  return fetchComments(allComments.unmatchedSignatures, marketClient)
    .then((comments) => {
      // Versions will be correct because they were sent down and consistent read done if not matching
      // Anything not returned is just missing from the DB for now
      addMarketsStructInfo('comments', marketsStruct, comments, marketId);
      marketsStruct['existingCommentIds'] = allComments.existingCommentIds;
    });
}

/**
 * The subset of investible fetch signatures the endpoint can actually serve. An entry with no
 * market_infos is rejected by market_investible_get_request_validator, which requires the
 * property, and the rejection takes down every other entry in the same body.
 */
export function fetchableInvestibleSignatures(unmatchedSignatures) {
  return (unmatchedSignatures || []).filter((signature) => !_.isEmpty(signature.market_infos));
}

function fetchMarketInvestibles(marketClient, marketId, allInvestibles, marketsStruct) {
  // T-all-2485: investiblesSignatureGenerator emits {investible} with no market_infos when an
  // investible version lands in the audit before its paired market_investible version does -
  // a race the burst hits for freshly created options. The endpoint requires market_infos, so
  // that entry is rejected, and because the schema validates the whole body every other
  // investible batched with it dies too. Sending it can never succeed, so drop it here and let
  // the market stay dirty: the signature is still unmatched, so the next cycle retries once the
  // paired indicator lands. Filtering in the generator instead would make the market look clean
  // and an investible whose own version changed alone would never be fetched at all.
  const fetchable = fetchableInvestibleSignatures(allInvestibles.unmatchedSignatures);
  if (_.isEmpty(fetchable)) {
    return Promise.resolve(true);
  }
  return fetchInvestibles(fetchable, marketClient)
    .then((investibles) => {
      addMarketsStructInfo('investibles', marketsStruct, investibles);
    });
}

function fetchMarketPresences(marketClient, marketId, allMp, marketsStruct) {
  const userSignatures = allMp.unmatchedSignatures.map(signature => {
    return {id: signature.id, version: signature.market_capability_version};
  });
  return getMarketUsers(userSignatures, marketClient)
    .then((users) => {
      const promises = [];
      users.forEach((user) => {
        const userRawSignature = allMp.unmatchedSignatures.find(signature => signature.id === user.id);
        const investmentSignatures = userRawSignature.investments;
        if (!_.isEmpty(investmentSignatures)) {
          promises.push(getInvestments(user.id, investmentSignatures, marketClient).then((investments) => {
            user.investments = investments;
          }));
        }
      });
      return new LimitedParallelMap(promises, (promise) => promise, MAX_CONCURRENT_API_CALLS)
        .then(() => addMarketsStructInfo('users', marketsStruct, users, marketId));
    });
}

function fetchMarketStages(marketClient, marketId, allMs, marketsStruct) {
  return getMarketStages(allMs.unmatchedSignatures, marketClient)
    .then((stages) => {
      addMarketsStructInfo('stages', marketsStruct, stages, marketId);
    });
}

function fetchMarketGroups(marketClient, marketId, allMg, marketsStruct) {
  return getMarketGroups(allMg.unmatchedSignatures, marketClient)
    .then((groups) => {
        addMarketsStructInfo('group', marketsStruct, groups, marketId);
    })
}

function fetchGroupMembers(marketClient, marketId, allMg, marketsStruct) {
  const mgSignatures = allMg.unmatchedSignatures;
  const signaturesByGroupId = {};
  mgSignatures.forEach((sign) => {
    const groupId = sign.group_id;
    if (!signaturesByGroupId[groupId]) {
      signaturesByGroupId[groupId] = [];
    }
    signaturesByGroupId[groupId].push({id: sign.id, version: sign.version});
  });
  return getGroupMembers(signaturesByGroupId, marketClient)
    .then((users) => {
      addMarketsStructInfo('members', marketsStruct, users);
    })
}
